import { useCallback, useRef, useState } from 'preact/hooks';
import { useUser } from '../hooks/UNSAFE_useUser';
import type {
  ItemHoverDetail,
  ItemFocusDetail,
  ItemInfo,
  PictoChartItemData,
  ItemDrillDetail
} from './pictoChart.type';
import { getItemInfo, isEqualItem } from './pictoChartUtils';
import { getRandomId } from '../utils/PRIVATE_visLayoutUtils/layoutUtils';
import { cancelEvent } from '../utils/UNSAFE_visUtils/eventsUtils';
import { useVisHover } from '#hooks/PRIVATE_useVisHover';
import { useVisDrill } from '#hooks/PRIVATE_useVisDrill';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

export function useEvents<K, D extends PictoChartItemData<K>>(
  touchResponse: 'touchStart' | 'touchHold',
  isInteractive: boolean,
  selectionMode: 'none' | 'single' | 'multiple',
  getDetailFromInfo: (info?: ItemInfo) => ItemHoverDetail<K, D> | ItemFocusDetail<K, D>,
  getPrevItemInfo: (info: ItemInfo) => ItemInfo,
  getNextItemInfo: (info: ItemInfo) => ItemInfo,
  drilling?: 'on' | 'off',
  onItemDrill?: (detail: ItemDrillDetail<K, D>) => void,
  onItemHover?: (detail: ItemHoverDetail<K, D>) => void,
  onItemFocus?: (detail: ItemFocusDetail<K, D>) => void
) {
  const [focusedItemInfo, setfocusedItemInfo] = useState<ItemInfo>({ idx: 0 });
  const [hoveredItemInfo, sethoveredItemInfo] = useState<ItemInfo>();

  const activeId = useRef<string>();

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const onHover = (event: PointerEvent) => {
    const itemInfo = getItemInfo(event.target as HTMLElement);
    if (!isEqualItem(itemInfo, hoveredItemInfo)) {
      sethoveredItemInfo(itemInfo && { ...itemInfo, isCurrent: true });
      activeId.current = getRandomId();
      if (isInteractive) {
        setfocusedItemInfo({ ...focusedItemInfo, isCurrent: false });
        onItemHover?.(getDetailFromInfo(itemInfo));
      }
    }
  };

  const onHoverLeave = () => {
    sethoveredItemInfo(undefined);
    activeId.current = undefined;
    if (isInteractive) {
      onItemHover?.(getDetailFromInfo());
    }
  };

  const hoverHandlers = useVisHover(onHover, undefined, onHoverLeave, touchResponse);

  const processDrill = useCallback(
    (info?: ItemInfo) => {
      const { id, data } = getDetailFromInfo(info);
      if (id != null && data) {
        onItemDrill?.({ id, data });
        activeId.current = getRandomId();
      }
    },
    [getDetailFromInfo, onItemDrill]
  );

  const onDrill = useCallback(
    (event: Event) => {
      if (event.type === 'keydown') {
        if (drilling == 'on') processDrill(focusedItemInfo);
      } else {
        processDrill(getItemInfo(event.target as HTMLElement));
      }
    },
    [drilling, focusedItemInfo, processDrill]
  );

  const drillProps = useVisDrill(selectionMode, onDrill);

  if (!isInteractive) {
    return {
      focusedItemInfo,
      hoveredItemInfo,
      // include relevant hover handlers for datatip
      eventsProps: hoverHandlers
    };
  }

  function updatefocusedItemInfo(itemInfo: ItemInfo) {
    onItemFocus?.(getDetailFromInfo(itemInfo));
    activeId.current = getRandomId();
    setfocusedItemInfo(itemInfo);
  }

  function handleKeyboardInput(item: ItemInfo) {
    if (!isEqualItem(item, focusedItemInfo)) {
      item.isCurrent = true;
      item.isFocusVisible = true;
      if (hoveredItemInfo) {
        sethoveredItemInfo({ ...hoveredItemInfo, isCurrent: false });
      }
      updatefocusedItemInfo(item);
    }
  }

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab':
        return;
      case 'ArrowDown': {
        const nextItemInfo = getNextItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
      case 'ArrowUp': {
        const nextItemInfo = getPrevItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
      case 'ArrowRight': {
        const nextItemInfo = isRtl
          ? getPrevItemInfo(focusedItemInfo)
          : getNextItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
      case 'ArrowLeft': {
        const nextItemInfo = isRtl
          ? getNextItemInfo(focusedItemInfo)
          : getPrevItemInfo(focusedItemInfo);
        handleKeyboardInput(nextItemInfo);
        cancelEvent(event);
        break;
      }
    }
  };

  const onKeyUp = (event: KeyboardEvent) => {
    const key = event.code;
    switch (key) {
      case 'Tab': {
        updatefocusedItemInfo({
          ...focusedItemInfo,
          isCurrent: true,
          isFocusVisible: true
        });
        cancelEvent(event);
        break;
      }
    }
  };

  const onBlur = () => {
    if (hoveredItemInfo != null || focusedItemInfo.isCurrent) {
      onItemFocus?.(getDetailFromInfo(undefined));
    }
    const itemInfo = { ...focusedItemInfo, isCurrent: false, isFocusVisible: false };
    setfocusedItemInfo(itemInfo);
  };

  return {
    focusedItemInfo,
    hoveredItemInfo,
    activeId: activeId.current,
    eventsProps: mergeProps(drillProps, hoverHandlers, {
      onKeyUp,
      onKeyDown,
      onBlur
    })
  };
}
